import React, {useCallback, useLayoutEffect, useMemo, useRef, useState} from 'react';
import {Button, Icon, MenuItem, Tag, Tooltip} from '@blueprintjs/core';
import {MultiSelect} from '@blueprintjs/select';
import styled from 'styled-components';

import {StyledFormGroup} from './shared';


const TargetButton = styled(Button)`
    &[class*="bp"][class*="-button"] {
        border-radius: var(--sf-border-radius) !important;
        width: ${p => p.fill ? '100%' : 'fit-content'};
        justify-content: space-between;
        background: transparent !important;
        box-shadow: inset 0 0 0 1px rgba(17, 20, 24, 0.2) !important;
        padding: 4px 8px;
        min-height: 30px;
        cursor: pointer;
        overflow: hidden;

        & > .bp6-button-text {
            display: flex;
            align-items: center;
            gap: 4px;
            overflow: hidden;
            flex: 1;
            min-width: 0;
        }
    }
`;

const ActionBar = styled.div`
    display: flex;
    gap: 4px;
    padding: 4px 5px;
    border-top: 1px solid rgba(17, 20, 24, 0.15);

    & > * { flex: 1; }
`;

const TagsDisplay = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    overflow: hidden;
    position: relative;
`;

const TagRemoveBtn = styled.span`
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    border-radius: 0 10px 10px 0;
    background: rgba(205, 66, 70, 0.75);
    color: white;
    font-size: 12px;
    line-height: 1;
    cursor: pointer;
    opacity: 0;
    transition: opacity 0.15s;
`;

const HoverRemoveTag = styled(Tag)`
    position: relative;
    &:hover ${TagRemoveBtn} {
        opacity: 1;
    }
`;

const MeasureRow = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    position: absolute;
    visibility: hidden;
    pointer-events: none;
    white-space: nowrap;
`;

const HiddenTagsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 4px;
`;

const AutoFitTags = ({selected, options, disabled, onRemove}) => {
    const measureRef = useRef(null);
    const targetRef = useRef(null);
    const fitRef = useRef(selected.length);
    const hiddenRef = useRef(0);
    const [, forceUpdate] = useState(0);

    const tags = useMemo(() =>
        selected.map(v => {
            const item = options.find(o => o.value === v);
            return {value: v, label: item?.label ?? v};
        }),
    [selected, options]);

    const compute = useCallback(() => {
        const measure = measureRef.current;
        const target = targetRef.current;
        if (!measure || !target || !tags.length) return;

        const available = target.clientWidth - 60;
        const children = measure.children;
        if (!children.length || available <= 0) return;

        const badgeWidth = children[children.length - 1].offsetWidth + 4;
        let used = 0;
        let fit = 0;

        for (let i = 0; i < tags.length; i++) {
            const tagWidth = children[i].offsetWidth + (i > 0 ? 4 : 0);
            const isLast = i === tags.length - 1;
            const spaceNeeded = used + tagWidth + (isLast ? 0 : badgeWidth);
            if (spaceNeeded > available && fit > 0) break;
            used += tagWidth + (i > 0 ? 4 : 0);
            fit++;
        }

        const hidden = tags.length - fit;
        if (fit !== fitRef.current || hidden !== hiddenRef.current) {
            fitRef.current = fit;
            hiddenRef.current = hidden;
            forceUpdate(n => n + 1);
        }
    }, [tags]);

    const refCallback = useCallback((node) => {
        measureRef.current = node;
        if (node) {
            const formGroup = node.closest('[class*="bp"][class*="-form-group"]');
            targetRef.current = formGroup || node.parentElement;
        }
    }, []);

    useLayoutEffect(() => {
        const target = targetRef.current;
        if (!target) return;
        compute();
        const ro = new ResizeObserver(compute);
        ro.observe(target);
        return () => ro.disconnect();
    }, [compute]);

    if (!tags.length) return null;

    const visibleCount = fitRef.current;
    const remaining = selected.length - visibleCount;

    return (
        <TagsDisplay>
            <MeasureRow ref={refCallback}>
                {tags.map(t =>
                    <Tag key={t.value} minimal small round intent="primary">{t.label}</Tag>
                )}
                <Tag minimal small round>+{selected.length}</Tag>
            </MeasureRow>
            {tags.slice(0, visibleCount).map(t =>
                <HoverRemoveTag key={t.value} minimal small round intent="primary">
                    {t.label}
                    {!disabled && onRemove && <TagRemoveBtn onClick={(e) => { e.stopPropagation(); onRemove(t.value); }}>&times;</TagRemoveBtn>}
                </HoverRemoveTag>
            )}
            {remaining > 0 && (
                <Tooltip
                    placement="bottom"
                    hoverOpenDelay={200}
                    content={
                        <HiddenTagsList>
                            {tags.map(t => <Tag key={t.value} small round>{t.label}</Tag>)}
                        </HiddenTagsList>
                    }
                >
                    <Tag minimal small round interactive>+{remaining}</Tag>
                </Tooltip>
            )}
        </TagsDisplay>
    );
};

const getDisplayText = (selectedItems, options, label) => {
    if (selectedItems.length === 0) return `No ${label}`;
    if (selectedItems.length === 1) {
        const item = options.find(o => o.value === selectedItems[0]);
        return item?.label ?? selectedItems[0];
    }
    if (selectedItems.length === options.length) return `All ${label}`;
    return `${selectedItems.length} ${label}`;
};

const filterItem = (query, item) => {
    if (!query) return true;
    return item.label.toLowerCase().includes(query.toLowerCase());
};

const MultiSelectField = ({label, rawLabel, value, options: initialOptions, disabled, error, icon, placeholder, allowCreate, nullable, fill, matchTargetWidth, showMax, onChange}) => {
    const intent = error ? 'danger' : 'none';
    const selected = value || [];
    const buttonLabel = rawLabel || label;
    const targetRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    // Merge any created values (in selected but not in initialOptions) into the options list
    const options = useMemo(() => {
        if (!allowCreate) return initialOptions;
        const known = new Set(initialOptions.map(o => o.value));
        const created = selected
            .filter(v => !known.has(v))
            .map(v => ({label: String(v), value: v}));
        return [...initialOptions, ...created];
    }, [initialOptions, selected, allowCreate]);

    // Sort: selected items first, then unselected; alphabetical within each group
    const sortedOptions = useMemo(() => {
        const selectedSet = new Set(selected);
        return [...options].sort((a, b) => {
            const aSelected = selectedSet.has(a.value) ? 0 : 1;
            const bSelected = selectedSet.has(b.value) ? 0 : 1;
            if (aSelected !== bSelected) return aSelected - bSelected;
            return a.label.localeCompare(b.label);
        });
    }, [options, selected]);

    const handleItemSelect = useCallback((item) => {
        if (selected.includes(item.value)) {
            const remaining = selected.filter(v => v !== item.value);
            onChange(nullable && remaining.length === 0 ? null : remaining);
        } else {
            onChange([...selected, item.value]);
        }
    }, [selected, nullable, onChange]);

    const handleRemoveOne = useCallback((val) => {
        const remaining = selected.filter(v => v !== val);
        onChange(nullable && remaining.length === 0 ? null : remaining);
    }, [selected, nullable, onChange]);

    const handleSelectAll = useCallback(() => {
        onChange(options.map(o => o.value));
    }, [options, onChange]);

    const handleClear = useCallback(() => {
        onChange(nullable ? null : []);
    }, [nullable, onChange]);

    const handleCreateItem = useCallback((query) => ({label: query, value: query}), []);
    const renderCreateItem = useCallback((query, active, handleClick) => (
        <MenuItem key="__create__" icon="add" text={`Create "${query}"`} active={active} onClick={handleClick} shouldDismissPopover={false} />
    ), []);

    const displayText = getDisplayText(selected, options, buttonLabel);

    // showMax: undefined → auto-fit, 0 → text only, null → all tags, N → up to N tags
    const displayContent = useMemo(() => {
        if (showMax === undefined) return null; // handled by AutoFitTags
        const max = showMax === null ? Infinity : (showMax || 0);
        if (!max || selected.length === 0) return displayText;
        const visible = selected.slice(0, max);
        const remaining = selected.length - visible.length;
        return (
            <TagsDisplay>
                {visible.map(v => {
                    const item = options.find(o => o.value === v);
                    return (
                        <HoverRemoveTag key={v} minimal small round intent="primary">
                            {item?.label ?? v}
                            {!disabled && <TagRemoveBtn onClick={(e) => { e.stopPropagation(); handleRemoveOne(v); }}>&times;</TagRemoveBtn>}
                        </HoverRemoveTag>
                    );
                })}
                {remaining > 0 && (
                    <Tooltip
                        placement="bottom"
                        hoverOpenDelay={200}
                        content={
                            <HiddenTagsList>
                                {selected.map(v => {
                                    const item = options.find(o => o.value === v);
                                    return <Tag key={v} small round>{item?.label ?? v}</Tag>;
                                })}
                            </HiddenTagsList>
                        }
                    >
                        <Tag minimal small round interactive>+{remaining}</Tag>
                    </Tooltip>
                )}
            </TagsDisplay>
        );
    }, [selected, options, showMax, displayText, disabled, handleRemoveOne]);

    const useAutoFit = showMax === undefined && selected.length > 0;
    const tagsContent = useAutoFit
        ? <AutoFitTags selected={selected} options={options} disabled={disabled} onRemove={handleRemoveOne} />
        : (displayContent ?? (selected.length === 0 ? null : displayText));

    // Selected items as item objects for Blueprint's MultiSelect
    const selectedItems = useMemo(() =>
        selected.map(v => options.find(o => o.value === v) || {label: String(v), value: v}),
    [selected, options]);

    const customTarget = useCallback((items, open) => (
        <TargetButton
            ref={targetRef}
            fill={fill}
            disabled={disabled}
            endIcon={open ? 'chevron-up' : 'chevron-down'}
            text={
                <>
                    {icon && <Icon icon={icon} size={14} style={{color: '#5c7080', flexShrink: 0}} />}
                    {tagsContent || <span style={{color: '#5c7080'}}>{displayText}</span>}
                </>
            }
        />
    ), [fill, disabled, icon, tagsContent, displayText]);

    return (
        <StyledFormGroup label={label} helperText={error?.message} intent={intent}>
            <MultiSelect
                items={sortedOptions}
                selectedItems={selectedItems}
                onItemSelect={handleItemSelect}
                resetOnSelect={true}
                onRemove={(item) => handleRemoveOne(item.value)}
                itemPredicate={filterItem}
                itemRenderer={(item, {handleClick, modifiers, index}) => {
                    if (!modifiers.matchesPredicate) return null;
                    return (
                        <MenuItem
                            key={`${item.value}-${index}`}
                            icon={selected.includes(item.value) ? 'tick' : 'blank'}
                            text={item.label}
                            active={modifiers.active}
                            disabled={modifiers.disabled}
                            onClick={handleClick}
                            shouldDismissPopover={false}
                        />
                    );
                }}
                tagRenderer={(item) => item.label}
                customTarget={customTarget}
                fill={fill}
                disabled={disabled}
                placeholder={placeholder || 'Search...'}
                createNewItemFromQuery={allowCreate ? handleCreateItem : undefined}
                createNewItemRenderer={allowCreate ? renderCreateItem : undefined}
                popoverProps={{
                    minimal: true,
                    placement: 'bottom-start',
                    matchTargetWidth: matchTargetWidth ?? !!fill,
                    isOpen,
                    onInteraction: (nextOpen) => setIsOpen(nextOpen),
                }}
                menuProps={{style: {maxHeight: 300, overflowY: 'auto'}}}
                tagInputProps={{
                    tagProps: {minimal: true, small: true, round: true, intent: 'primary'},
                    leftIcon: icon,
                }}
                itemListRenderer={({items, renderItem, renderCreateItem, itemsParentRef}) => {
                    const createItem = renderCreateItem();
                    return (
                        <div>
                            <div style={{maxHeight: 300, overflowY: 'auto'}} ref={itemsParentRef}>
                                {items.length > 0
                                    ? items.map(renderItem)
                                    : !createItem && <MenuItem disabled text="No results." />
                                }
                                {createItem}
                            </div>
                            <ActionBar>
                                <Button small outlined text="Select all" onClick={handleSelectAll} />
                                <Button small outlined text="Clear" onClick={handleClear} />
                            </ActionBar>
                        </div>
                    );
                }}
            />
        </StyledFormGroup>
    );
};

export {MultiSelectField};
